'use client';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

export function SmartBot() {
  const pathname = usePathname();
  const hasLoadedScriptRef = useRef(false);

  // Load Mavenoid script with proper timing and performance optimization
  useEffect(() => {
    // Return early if script was already loaded
    if (hasLoadedScriptRef.current) return;
    
    // Mark as loaded to prevent duplicate attempts
    hasLoadedScriptRef.current = true;

    // Set up the script loading with a small delay to avoid initial load contention
    const loadTimeout = setTimeout(() => {
      try {
        // Create script elements with required structure
        const id = 'mavenoid-script';
        const preScriptId = 'pre-' + id;
        
        // Create pre-script element (required by Mavenoid)
        const preScript = document.createElement('script');
        preScript.id = preScriptId;
        document.body.appendChild(preScript);
        
        // Initialize Mavenoid array
        window['mavenoid'] = window['mavenoid'] || [];
        
        // Create main script element
        const script = document.createElement('script');
        script.id = id;
        script.async = true; // Allow async loading
        script.src = 'https://widget-hosts.mavenoid.com/custom-embedding-scripts/frontpoint-product-selector.js';
        
        // Add error handling
        script.onerror = (error) => {
          console.error('SmartBot: Failed to load script', error);
        };
        
        // Insert the script after the pre-script
        preScript.parentNode?.insertBefore(script, preScript);
      } catch (error) {
        console.error('SmartBot: Error initializing script', error);
      }
    }, 1500); // Small delay to prioritize initial page content
    
    return () => {
      clearTimeout(loadTimeout);
    };
  }, []);

  // Handle visibility based on page path
  useEffect(() => {
    const isAddonsPage = pathname.includes('add-ons');
    
    // Function to toggle visibility
    const toggleVisibility = () => {
      // Get all instances that might be present
      const assistantElements = document.querySelectorAll('mavenoid-assistant');
      
      if (assistantElements.length > 0) {
        assistantElements.forEach(element => {
          if (isAddonsPage) {
            element.classList.add('hidden');
          } else {
            element.classList.remove('hidden');
          }
        });
        return true;
      }
      return false;
    };
    
    // Try immediately first
    if (toggleVisibility()) return;
    
    // If not found, set up periodic checks for when the element appears
    const checkInterval = setInterval(() => {
      if (toggleVisibility()) {
        clearInterval(checkInterval);
      }
    }, 1000);
    
    // Clean up after 10 seconds maximum to avoid continuous checking
    const cleanupTimeout = setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);
    
    return () => {
      clearInterval(checkInterval);
      clearTimeout(cleanupTimeout);
    };
  }, [pathname]);

  // Return empty fragment - no visible content
  return null;
}
